import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { WizardModal, type WizardPrimaryAction } from '~/components/overlays';
import { wizardStepsFromTranslations } from '~/components/overlays/wizardStepsFromTranslations';
import { toastGraphQLError } from '~/utils';
import { useSubmitOnce } from '~/hooks/useSubmitOnce';
import { useCreateRequestForQuoteFromBuyList } from '~/api/requestForQuote';
import { useBuyListItemSelection } from '~/pages/buy-lists/sections/useBuyListItemSelection';
import { useSummaryProducts } from '~/pages/buy-lists/sections/useSummaryProducts';
import { SelectBuyListItemsStep } from '~/pages/buy-lists/sections/SelectBuyListItemsStep';
import { SelectSellerContactsStep } from './SelectSellerContactsStep';
import { RequestForQuoteSummaryStep } from './RequestForQuoteSummaryStep';
import { useSellerRecipients } from './useSellerRecipients';
import type { BuyListDetailItem } from '~/pages/buy-lists/sections/types';
import type { BuyListDetailTally } from '~/pages/buy-lists/sections/tallies/types';

type RequestForQuoteModalProps = {
  onClose: () => void;
  buyListId: string;
  items: BuyListDetailItem[];
  tallies: BuyListDetailTally[];
  expectedShipDate: string;
};

const STEP_IDS = ['items', 'contacts', 'summary'] as const;

type Step = (typeof STEP_IDS)[number];

export function RequestForQuoteModal({
  onClose,
  buyListId,
  items,
  tallies,
  expectedShipDate,
}: Readonly<RequestForQuoteModalProps>) {
  const { t } = useTranslation('inventory');
  const [step, setStep] = useState<Step>('items');
  const selection = useBuyListItemSelection(items, tallies);
  const products = useSummaryProducts(selection);

  const recipients = useSellerRecipients();
  const { selectedOrganizationIds, sellerInvitations, recipientGroups } =
    recipients;

  const { mutate, isPending } = useCreateRequestForQuoteFromBuyList();
  const submitOnce = useSubmitOnce();

  const handleSend = () => {
    submitOnce((onSettled) => {
      mutate(
        {
          buyListId,
          ...selection.toSelectionInputs(),
          sellerInvitations,
        },
        {
          onSuccess: (data) => {
            toast.success(
              t('inventory.buy_list.request_for_quote.success', {
                count: data.createRequestForQuoteFromBuyList.length,
              })
            );
            onClose();
          },
          onError: toastGraphQLError,
          onSettled,
        }
      );
    });
  };

  const steps = wizardStepsFromTranslations(
    t,
    'inventory.buy_list.request_for_quote',
    STEP_IDS
  );

  const primaryActionByStep: Record<Step, WizardPrimaryAction> = {
    items: {
      label: t('inventory.buy_list.request_for_quote.continue'),
      testId: 'rfq-continue',
      disabled:
        selection.hasNothingSelected || selection.hasLineWithoutQuantity,
      onClick: () => setStep('contacts'),
    },
    contacts: {
      label: t('inventory.buy_list.request_for_quote.next'),
      testId: 'rfq-next',
      disabled: selectedOrganizationIds.size === 0,
      onClick: () => setStep('summary'),
    },
    summary: {
      label: t('inventory.buy_list.request_for_quote.send'),
      testId: 'rfq-send',
      loading: isPending,
      disabled: isPending || selection.hasNothingSelected,
      onClick: handleSend,
    },
  };

  return (
    <WizardModal
      onClose={onClose}
      isPending={isPending}
      steps={steps}
      currentStepId={step}
      onStepChange={setStep}
      testIdPrefix="rfq"
      backLabel={t('inventory.buy_list.request_for_quote.back')}
      primaryAction={primaryActionByStep[step]}
    >
      {step === 'items' && (
        <SelectBuyListItemsStep
          items={items}
          {...selection.itemsStepProps}
          tallySelection={selection.talliesSectionProps}
        />
      )}
      {step === 'contacts' && (
        <SelectSellerContactsStep
          recipients={recipients}
          isSubmitting={isPending}
        />
      )}
      {step === 'summary' && (
        <RequestForQuoteSummaryStep
          products={products}
          recipientGroups={recipientGroups}
          expectedShipDate={expectedShipDate}
        />
      )}
    </WizardModal>
  );
}
